import { AxiosResponse } from "axios";
import { axiosInstance } from "./common";

export enum EventType {
  ACCOUNT_RED_FLAGGED = "ACCOUNT_RED_FLAGGED",
  CASE_OPENED = "CASE_OPENED",
  CASE_ASSIGNED_TO_INVESTIGATOR = "CASE_ASSIGNED_TO_INVESTIGATOR",
  INVESTIGATION_COMPLETED = "INVESTIGATION_COMPLETED",
  ZFRC_MEETING_HELD = "ZFRC_MEETING_HELD",
  FIC_REVIEW_MEETING_HELD = "FIC_REVIEW_MEETING_HELD",
  SCN_ISSUED = "SCN_ISSUED",
  SCN_DELIVERED = "SCN_DELIVERED",
  SCN_RETURNED = "SCN_RETURNED",
  SCN_RESPONSE_RECEIVED = "SCN_RESPONSE_RECEIVED",
  FIC_FRAUD_CLASSIFICATION_COMPLETED = "FIC_FRAUD_CLASSIFICATION_COMPLETED",
  REASONED_ORDER_ISSUED = "REASONED_ORDER_ISSUED",
  SARFAESI_NOTICE_13_2_ISSUED = "SARFAESI_NOTICE_13_2_ISSUED",
  SARFAESI_NOTICE_13_4_ISSUED = "SARFAESI_NOTICE_13_4_ISSUED",
  SECURITY_PHYSICALLY_POSSESSED = "SECURITY_PHYSICALLY_POSSESSED",
  RECOVERY_SUIT_FILED_IN_DRT = "RECOVERY_SUIT_FILED_IN_DRT",
  CASE_FILED_IN_NCLT = "CASE_FILED_IN_NCLT",
  FMR1_FILED = "FMR1_FILED",
  COMPLAINT_LODGED_WITH_LEA = "COMPLAINT_LODGED_WITH_LEA",
  FIR_FILED_WITH_LEA = "FIR_FILED_WITH_LEA",
  FRMD_NOTE_PRESENTED_TO_BOARD = "FRMD_NOTE_PRESENTED_TO_BOARD",
  BOARD_RECOMMENDATIONS_RECEIVED = "BOARD_RECOMMENDATIONS_RECEIVED",
  CIRCULAR_ISSUED = "CIRCULAR_ISSUED",
  FMR_WITHDRAWAL_VERIFICATION_COMPLETED = "FMR_WITHDRAWAL_VERIFICATION_COMPLETED",
  FMR_WITHDRAWAL_COMMITTEE_CONSTITUTED = "FMR_WITHDRAWAL_COMMITTEE_CONSTITUTED",
  FMR_WITHDRAWAL_APPROVED = "FMR_WITHDRAWAL_APPROVED",
  FMR_WITHDRAWN = "FMR_WITHDRAWN",
  STATISTICAL_CLOSURE_COMMITTEE_CONSTITUTED = "STATISTICAL_CLOSURE_COMMITTEE_CONSTITUTED",
  RBI_CLOSURE_COMMITTEE_CONSTITUTED = "RBI_CLOSURE_COMMITTEE_CONSTITUTED",
  STATISTICAL_CLOSURE_FROM_RBI_APPROVED = "STATISTICAL_CLOSURE_FROM_RBI_APPROVED",
  STATISTICAL_CLOSURE_RECORDED_IN_CIMS = "STATISTICAL_CLOSURE_RECORDED_IN_CIMS",
  FINAL_CASE_CLOSURE_WITH_RBI = "FINAL_CASE_CLOSURE_WITH_RBI",
  DATE_OF_OCCURENCE = "DATE_OF_OCCURENCE",
  DATE_OF_DETECTION = "DATE_OF_DETECTION",
  DATE_OF_CLASSIFICATION_CA_APPROVAL = "DATE_OF_CLASSIFICATION_CA_APPROVAL",
  HIA_RECOMMENDATIONS = "HIA_RECOMMENDATIONS",
  ZI_RECOMMENDATIONS = "ZI_RECOMMENDATIONS",
  CASE_DEALING_OFFICER_ASSIGNED = "CASE_DEALING_OFFICER_ASSIGNED",
  IO_APPOINTING_AUTHORITY_ASSIGNED = "IO_APPOINTING_AUTHORITY_ASSIGNED",
}

export enum EventEntityType {
  CASE = "CASE",
  PARTY_INVOLVED = "PARTY_INVOLVED",
  LOAN_ACCOUNT = "LOAN_ACCOUNT",
  LEA = "LEA",
  COMMITTEE = "COMMITTEE",
  PRE_SCN_FIC_MEETING = "PRE_SCN_FIC_MEETING",
  POST_IR_ASSESSMENT = "POST_IR_ASSESSMENT",
}

export enum LEAType {
  CBI = "CBI",
  STATE_POLICE = "STATE_POLICE",
  CYBER_POLICE = "CYBER_POLICE",
  // SFIO = "SFIO",
}

export enum CommitteeType {
  ABBFF = "ABBFF",
  ACB = "ACB",
  BOARD = "BOARD",
  SCBMF = "SCBMF",
  VIGILANCE_COMMITTEE = "VIGILANCE_COMMITTEE",
}

export enum EventStatus {
  ACTIVE = "ACTIVE",
  DELETED = "DELETED",
}

export interface CaseEvent {
  event_id: string;
  event_type: EventType;
  actor_email: string;
  entity_type: EventEntityType;
  entity_id: string;
  date: string;
  created_at: string;
  last_updated_at: string;
  status: EventStatus;
}

export interface CreateCaseEvent {
  actor_email: string;
  entity_type: EventEntityType;
  entity_id: string;
  date: string;
}

export interface BulkCaseEventCreateRequest {
  event_type: EventType;
  events: CreateCaseEvent[];
  notes?: string | null;
}

export interface EventStage {
  event_type: EventType;
  notes?: string | null;
}

export interface CaseEventsResponse {
  events: CaseEvent[];
  event_stages: EventStage[];
}

export const CaseEventsAPI = {
  addCaseEvents: async (
    case_id: string,
    request: BulkCaseEventCreateRequest
  ) => {
    const response: AxiosResponse<CaseEventsResponse> =
      await axiosInstance.post(`/v1/cases/${case_id}/event-stage`, request);

    return response.data;
  },
};

export const EventTypeLabelMap: Record<EventType, string> = {
  [EventType.ACCOUNT_RED_FLAGGED]: "Account Red Flagged",
  [EventType.CASE_OPENED]: "Case Opened",
  [EventType.CASE_ASSIGNED_TO_INVESTIGATOR]: "Investigating Officer Assigned",
  [EventType.INVESTIGATION_COMPLETED]: "Investigation Report Submitted",
  [EventType.ZFRC_MEETING_HELD]: "ZFRC Meeting Held",
  [EventType.FIC_REVIEW_MEETING_HELD]: "FIC for Decision On No-Fraud or Issuance of SCN",
  [EventType.SCN_ISSUED]: "Show Cause Notice Issued",
  [EventType.SCN_DELIVERED]: "Show Cause Notice Delivered",
  [EventType.SCN_RETURNED]: "Show Cause Notice Returned",
  [EventType.SCN_RESPONSE_RECEIVED]: "Show Cause Notice Response Received",
  [EventType.FIC_FRAUD_CLASSIFICATION_COMPLETED]: "FIC for Final Decision & Recommendation",
  [EventType.REASONED_ORDER_ISSUED]: "Reasoned Order Issued",
  [EventType.SARFAESI_NOTICE_13_2_ISSUED]: "SARFAESI Notice u/s 13(2) Issued",
  [EventType.SARFAESI_NOTICE_13_4_ISSUED]: "SARFAESI Notice u/s 13(4) Issued",
  [EventType.SECURITY_PHYSICALLY_POSSESSED]: "Security Physically Possessed",
  [EventType.RECOVERY_SUIT_FILED_IN_DRT]: "Recovery Suit Filed in DRT",
  [EventType.CASE_FILED_IN_NCLT]: "Case Filed in NCLT",
  [EventType.FMR1_FILED]: "FMR-1 Filed",
  [EventType.COMPLAINT_LODGED_WITH_LEA]: "Complaint Lodged with LEA",
  [EventType.FIR_FILED_WITH_LEA]: "FIR Filed with LEA",
  [EventType.FRMD_NOTE_PRESENTED_TO_BOARD]: "FRMD Note Presented to Board",
  [EventType.BOARD_RECOMMENDATIONS_RECEIVED]: "Board Recommendations Received",
  [EventType.CIRCULAR_ISSUED]: "Circular Issued",
  [EventType.FMR_WITHDRAWAL_VERIFICATION_COMPLETED]: "FMR Withdrawal Verification Completed",
  [EventType.FMR_WITHDRAWAL_COMMITTEE_CONSTITUTED]: "FMR Withdrawal Committee Constituted",
  [EventType.FMR_WITHDRAWAL_APPROVED]: "FMR Withdrawal Approved",
  [EventType.FMR_WITHDRAWN]: "FMR Withdrawn",
  [EventType.STATISTICAL_CLOSURE_COMMITTEE_CONSTITUTED]: "Statistical Closure Committee Constituted",
  [EventType.RBI_CLOSURE_COMMITTEE_CONSTITUTED]: "RBI Closure Committee Constituted",
  [EventType.STATISTICAL_CLOSURE_FROM_RBI_APPROVED]: "Statistical Closure from RBI Approved",
  [EventType.STATISTICAL_CLOSURE_RECORDED_IN_CIMS]:"Statistical Closure Recorded in CIMS",
  [EventType.FINAL_CASE_CLOSURE_WITH_RBI]: "Final Case Closure with RBI",
  [EventType.DATE_OF_OCCURENCE]: "Date of Occurrence",
  [EventType.DATE_OF_DETECTION]: "Date of Detection",
  [EventType.DATE_OF_CLASSIFICATION_CA_APPROVAL]: "Date of Classification (Approval from Competent Authority)",
  [EventType.HIA_RECOMMENDATIONS]: "Head of Internal Audit (HIA) Recommendations",
  [EventType.ZI_RECOMMENDATIONS]: "Zonal Inspectorate Recommendations",
  [EventType.CASE_DEALING_OFFICER_ASSIGNED]: "Dealing Officer Assigned",
  [EventType.IO_APPOINTING_AUTHORITY_ASSIGNED]: "IO Appointing Authority Assigned"
};

export const LEATypeLabelMap: Record<LEAType, string> = {
  [LEAType.CBI]: "CBI",
  [LEAType.STATE_POLICE]: "State/UT Police",
  [LEAType.CYBER_POLICE]: "Cyber Police",
  // [LEAType.SFIO]: "SFIO",
};

export const CommitteeTypeLabelMap: Record<CommitteeType, string> = {
  [CommitteeType.ABBFF]: "ABBFF",
  [CommitteeType.ACB]: "ACB",
  [CommitteeType.BOARD]: "Board",
  [CommitteeType.SCBMF]: "SCBMF",
  [CommitteeType.VIGILANCE_COMMITTEE]: "Vigilance Committee",
};
